import {
	PropsWithChildren,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from "react";
import { useAuthProvider } from "../auth/hooks/auth-provider";
import { FreelancerOnboardingStatus } from "../models/enums/FreelancerOnboardingStatus";

export type InternalUserContextType = {
	readonly selectedFreelancerOnboardingStatus: FreelancerOnboardingStatus | "All";
	readonly isUIDisabled: boolean;
	setSelectedFreelancerOnboardingStatus: (
		freelancerOnboardingStatus: FreelancerOnboardingStatus | "All"
	) => void;
	setIsUIDisabled: (disabled: boolean) => void;
};

const InternalUserContext = createContext<InternalUserContextType | undefined>(undefined);

export const InternalUserProvider = ({ children }: PropsWithChildren<object>) => {
	// Use auth provider to get user info from token (no API call)
	const { user, isAuthenticated } = useAuthProvider();

	const getInitialStatus = (): FreelancerOnboardingStatus | "All" => {
		if (!isAuthenticated || !user?.externalId) return "All";

		// Use externalId for storage key since id comes from API
		const statusKey = `zoulloo_selectedFreelancerOnboardingStatus_user_${user.externalId}`;
		const storedStatus = localStorage.getItem(statusKey);

		if (storedStatus) {
			return storedStatus as FreelancerOnboardingStatus | "All";
		}

		const storageKey = `zoulloo_internal_menu_active_item_user_${user.externalId}`;
		const storedItem = sessionStorage.getItem(storageKey);

		if (storedItem?.startsWith("applications-")) {
			const status = storedItem.split("-")[1];

			return status as FreelancerOnboardingStatus | "All";
		}

		return "All";
	};

	const [selectedFreelancerOnboardingStatus, setSelectedFreelancerOnboardingStatus] =
		useState<FreelancerOnboardingStatus | "All">("All");
	const [isUIDisabled, setIsUIDisabled] = useState<boolean>(false);

	useEffect(() => {
		setSelectedFreelancerOnboardingStatus(getInitialStatus());
	}, [isAuthenticated, user]);

	const handleSetSelectedFreelancerOnboardingStatus = useCallback(
		(freelancerOnboardingStatus: FreelancerOnboardingStatus | "All") => {
			setSelectedFreelancerOnboardingStatus(freelancerOnboardingStatus);

			if (isAuthenticated && user?.externalId) {
				const statusKey = `zoulloo_selectedFreelancerOnboardingStatus_user_${user.externalId}`;
				localStorage.setItem(statusKey, freelancerOnboardingStatus.toString());
			}
		},
		[isAuthenticated, user]
	);

	const handleSetIsUIDisabled = useCallback((disabled: boolean) => {
		setIsUIDisabled(disabled);
	}, []);

	const contextValue = useMemo(
		() => ({
			selectedFreelancerOnboardingStatus,
			isUIDisabled,
			setSelectedFreelancerOnboardingStatus: handleSetSelectedFreelancerOnboardingStatus,
			setIsUIDisabled: handleSetIsUIDisabled
		}),
		[
			selectedFreelancerOnboardingStatus,
			isUIDisabled,
			handleSetSelectedFreelancerOnboardingStatus,
			handleSetIsUIDisabled
		]
	);

	return (
		<InternalUserContext.Provider value={contextValue}>
			{children}
		</InternalUserContext.Provider>
	);
};

export const useInternalUserContext = () => {
	const context = useContext(InternalUserContext);

	if (!context) {
		throw new Error("useInternalUserContext must be used within InternalUserContext");
	}

	return context;
};
